import { assert } from '@ember/debug';
import { isEmpty } from '@ember/utils';

import { doesNotExist, hasCorrectType } from './helpers';

/**
 * Validates that the provided argument has no value (is `undefined` or `null`).
 * Supports three validation modes:
 * 1. Legacy validation (no forbiddingArgValue): Always validates
 * 2. Existence-based (forbiddingArgValue, no when): Validates when forbidding arg exists
 * 3. Value-based (forbiddingArgValue + when): Validates when forbidding arg matches any value in 'when' array
 *
 * @param {object} validationData - An object containing:
 *   - `argument`: The forbidden argument value to check.
 *   - `forbiddingArgValue`: Optional - the value of the argument that forbids this one.
 *   - `when`: Optional - array of values that the forbiddingArgValue must match to trigger validation.
 *   - `message`: The error message to display if validation fails.
 */
export function forbiddenAssertion(validationData) {
  let { argument, forbiddingArgValue, message, when } = validationData;
  let isLegacyValidation = !('forbiddingArgValue' in validationData);
  let hasForbiddingArg = !isLegacyValidation && !doesNotExist(forbiddingArgValue);
  let hasConditionalValues = 'when' in validationData && Array.isArray(when) && when.length > 0;
  let shouldForbid = hasConditionalValues && when.includes(forbiddingArgValue);
  let hasForbiddingArgWithoutCondition = hasForbiddingArg && !hasConditionalValues;
  let shouldValidate = isLegacyValidation || shouldForbid || hasForbiddingArgWithoutCondition;

  if (shouldValidate) {
    assert(message, doesNotExist(argument));
  }
}

/**
 * optionalAssertion validates that the argument either has the expected data type
 * or has not been provided
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes message, type, argument
 */
export function optionalAssertion({ argument, message, type }) {
  assert(message, typeof hasCorrectType(argument, type) || doesNotExist(argument));
}

/**
 * requiredAssertion validates that the provided argument exists and has the expected data type
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, message, type
 */
export function requiredAssertion({ argument, message, type }) {
  assert(message, typeof hasCorrectType(argument, type) && !isEmpty(argument));
}

/**
 * optionalConfigOption validates that the provided configuration option is either
 * one of the expected values or has not been provided
 * if the config option fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, options, name, component
 */
export function optionalConfigOption({ argument, component, name, options }) {
  assert(
    `@${name} argument of ${component} must be one of ${options.join(', ')} or empty`,
    options.includes(argument) || doesNotExist(argument)
  );
}

/**
 * requiredConfigOption validates that the provided argument is one of the values in options
 * if the argument fails this assertion, the provided message is thrown as an Error
 * @param {object} param0 with attributes argument, component, name, options
 */
export function requiredConfigOption({ argument, component, name, options }) {
  assert(
    `@${name} argument of ${component} must be one of ${options.join(', ')}`,
    options.includes(argument) && !isEmpty(argument)
  );
}

// Assertion configuration objects
export const configOptionAssertions = {
  forbidden: forbiddenAssertion,
  optional: optionalConfigOption,
  required: requiredConfigOption,
};

export const defaultAssertions = {
  forbidden: forbiddenAssertion,
  optional: optionalAssertion,
  required: requiredAssertion,
};

export default {
  forbiddenAssertion,
  optionalAssertion,
  requiredAssertion,
  optionalConfigOption,
  requiredConfigOption,
  configOptionAssertions,
  defaultAssertions,
};
